/**
 * Danh mục quyền dùng chung (04/11/2026) — nguồn duy nhất cho bảng Phân quyền (RoleView / RoleList / RoleFormPage),
 * mẫu quyền và kiểm thử. Quyền thật của từng vai trò nằm trong bảng `roles.permissions`; mã nguồn không kiểm theo
 * tên vai trò.
 *
 * - `print` (In): mở bản in / tải PDF chứng từ của module. Chỉ có khi được tích (không tự suy từ Xem).
 * - `export` (Xuất DL): xuất Excel danh sách. Mặc định chỉ Ban Giám đốc, Kế toán, Admin.
 */
export const PERMISSION_ACTIONS = [
  { id: 'view', label: 'Xem' },
  { id: 'create', label: 'Thêm' },
  { id: 'update', label: 'Sửa' },
  { id: 'delete', label: 'Xóa' },
  { id: 'review', label: 'Kiểm tra' },
  { id: 'approve', label: 'Phê duyệt' },
  { id: 'print', label: 'In' },
  { id: 'export', label: 'Xuất DL' },
  { id: 'import', label: 'Nhập DL' },
] as const;

export type PermissionActionId = (typeof PERMISSION_ACTIONS)[number]['id'];

/** iconKey: tên biểu tượng cho giao diện (ánh xạ ở web), không phải dữ liệu nghiệp vụ */
export const PERMISSION_MODULE_GROUPS = [
  {
    group: 'Kinh Doanh',
    iconKey: 'sales',
    color: 'bg-blue-500',
    modules: [
      { id: 'customers', name: 'Khách hàng' },
      { id: 'quotations', name: 'Báo giá' },
      { id: 'orders', name: 'Đơn hàng' },
      { id: 'deliveries', name: 'Giao hàng' },
      { id: 'products', name: 'Sản phẩm' },
      { id: 'product-types', name: 'Loại sản phẩm' },
    ],
  },
  {
    group: 'Kế Hoạch & Mua Hàng',
    iconKey: 'planning',
    color: 'bg-indigo-500',
    modules: [
      { id: 'production-orders', name: 'Lệnh sản xuất' },
      { id: 'material-planning', name: 'KH Vật tư' },
      { id: 'process-planning', name: 'KH Công đoạn' },
      { id: 'purchasing', name: 'ĐH Vật tư' },
      { id: 'outsource-orders', name: 'ĐH Gia công' },
      { id: 'vendors', name: 'Nhà cung cấp' },
    ],
  },
  {
    group: 'Tài Chính',
    iconKey: 'finance',
    color: 'bg-emerald-500',
    modules: [
      { id: 'receipts', name: 'Thu chi' },
      { id: 'debts', name: 'Công nợ KH' },
    ],
  },
  {
    group: 'Sản Xuất',
    iconKey: 'factory',
    color: 'bg-orange-500',
    modules: [
      { id: 'machine-logs', name: 'Nhật ký máy' },
      { id: 'wip-transfer', name: 'Bàn giao BTP' },
    ],
  },
  {
    group: 'Kỹ Thuật',
    iconKey: 'tech',
    color: 'bg-purple-500',
    modules: [
      { id: 'specs', name: 'Tiêu chuẩn SP' },
      { id: 'layouts', name: 'Bình bản' },
      { id: 'molds', name: 'Khuôn' },
      { id: 'plates', name: 'Bản in Kẽm' },
      { id: 'ctp-logs', name: 'Nhật ký CTP' },
    ],
  },
  {
    group: 'Chất Lượng',
    iconKey: 'quality',
    color: 'bg-green-500',
    modules: [
      { id: 'qc-materials', name: 'QC NVL' },
      { id: 'qc-process', name: 'QC công đoạn' },
      { id: 'qc-final', name: 'QC thành phẩm' },
      { id: 'ncr', name: 'NCR / CAPA' },
    ],
  },
  {
    group: 'Kho',
    iconKey: 'warehouse',
    color: 'bg-cyan-500',
    modules: [
      { id: 'material-warehouse', name: 'Kho VT' },
      { id: 'fg-inventory', name: 'Kho TP' },
      { id: 'materials', name: 'Vật tư' },
      { id: 'material-types', name: 'Loại vật tư' },
    ],
  },
  {
    group: 'Hệ thống',
    iconKey: 'system',
    color: 'bg-slate-600',
    modules: [
      { id: 'company-info', name: 'Công ty' },
      { id: 'users', name: 'Người dùng' },
      { id: 'roles', name: 'Phân quyền' },
      { id: 'machines', name: 'Thiết bị' },
      { id: 'processes', name: 'Quy trình SX' },
      { id: 'workflows', name: 'Quy trình WF' },
      { id: 'configs', name: 'Cấu hình' },
      { id: 'reports', name: 'Báo cáo & Thống kê' },
      { id: 'trash', name: 'Thùng rác' },
      { id: 'feedback', name: 'Góp ý & Hỗ trợ' },
    ],
  },
] as const;

export const PERMISSION_MODULE_IDS: string[] = PERMISSION_MODULE_GROUPS.flatMap((g) => g.modules.map((m) => m.id));

/** Module quản trị hệ thống — mẫu quyền cho nhân viên thường không gồm các module này */
export const SYSTEM_PERMISSION_MODULES = ['company-info', 'users', 'roles', 'processes', 'workflows', 'configs', 'trash'] as const;

export function permissionModuleName(moduleId: string): string {
  for (const g of PERMISSION_MODULE_GROUPS) {
    const m = g.modules.find((x) => x.id === moduleId);
    if (m) return m.name;
  }
  return moduleId;
}

export function permissionActionLabel(actionId: string): string {
  return PERMISSION_ACTIONS.find((a) => a.id === actionId)?.label || actionId;
}

/**
 * Đường dẫn ngoài menu → module phân quyền (quyền In / Xuất DL theo màn đang mở). Bổ sung cho bảng menu
 * (apps/web/src/config/navigation.ts); khớp tiền tố dài nhất.
 */
export const PERMISSION_ROUTE_MODULES: Array<{ path: string; module: string }> = [
  { path: '/deliveries', module: 'deliveries' },
  { path: '/finance/ar', module: 'debts' },
  { path: '/finance/ap', module: 'debts' },
  { path: '/finance/ar-aging', module: 'debts' },
  { path: '/sales/debts', module: 'debts' },
  { path: '/finance', module: 'receipts' },
  { path: '/machine-logs', module: 'machine-logs' },
  { path: '/production-logs', module: 'machine-logs' },
  { path: '/shopfloor', module: 'machine-logs' },
  { path: '/mes', module: 'machine-logs' },
  { path: '/master/tooling', module: 'molds' },
  { path: '/tooling-tracking-board', module: 'molds' },
  { path: '/production/prepress', module: 'layouts' },
  { path: '/production/ctp-tracking', module: 'ctp-logs' },
  { path: '/production', module: 'production-orders' },
  { path: '/qc-check', module: 'qc-process' },
  { path: '/quality/iqc', module: 'qc-materials' },
  { path: '/quality/materials', module: 'qc-materials' },
  { path: '/quality/reports', module: 'reports' },
  { path: '/qc', module: 'reports' },
  { path: '/stocktakes', module: 'material-warehouse' },
  { path: '/stock-card', module: 'material-warehouse' },
  { path: '/transactions', module: 'material-warehouse' },
  { path: '/warehouse', module: 'material-warehouse' },
  { path: '/material-supply-board', module: 'material-warehouse' },
  { path: '/wip-transfer', module: 'wip-transfer' },
  { path: '/wip-transfers', module: 'wip-transfer' },
  { path: '/planning/mrp', module: 'material-planning' },
  { path: '/planning/paper-allocation', module: 'material-planning' },
  { path: '/production-scheduling', module: 'process-planning' },
  { path: '/factory/process-planning', module: 'process-planning' },
  { path: '/purchase-orders', module: 'purchasing' },
  { path: '/estimating', module: 'quotations' },
  { path: '/pricing-configs', module: 'quotations' },
  { path: '/analytics', module: 'reports' },
  { path: '/executive', module: 'reports' },
  { path: '/audit-logs', module: 'reports' },
  { path: '/feedback', module: 'feedback' },
];

/** Màn cá nhân / hướng dẫn: in không cần quyền (Hướng dẫn sử dụng, Lịch sử cập nhật, hồ sơ cá nhân) */
export const PERMISSION_FREE_ROUTES = ['/guide', '/changelog', '/project-brief', '/profile', '/settings', '/my-requests'];

/** Mã đặc biệt: màn không cần quyền In */
export const PERMISSION_FREE_MODULE = '__free';
